<script setup lang="ts">
/**
 * Single-card cell for the mobile subscription types list: name, a meta line
 * (days, visit count or "∞"), the covered service types, and the price on the
 * right (see `docs/sketches/mobile/service-types-table.html`).
 */
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import { formatMoney, currencySymbol } from '@/utils/formatMoney'

const props = defineProps<{
  index: number
  name: string
  durationDays: number
  numberOfServices: number | null
  coveredTypes: string
  price?: number | null
}>()

const { t } = useI18n()

const priceLabel = computed(() => {
  if (props.price == null || Number.isNaN(props.price)) return null
  return formatMoney({ amount: props.price, precision: 0, fractionDigits: 2 }).formatted
})
</script>

<template>
  <td class="sub-mobile font-tufte">
    <div class="sub-mobile__row">
      <span class="sub-mobile__idx">{{ index }}</span>
      <div class="sub-mobile__main">
        <div class="sub-mobile__name">{{ name }}</div>
        <div class="sub-mobile__meta">
          <span
            ><span class="sub-mobile__n">{{ durationDays }}</span> {{ t('common.daysShort') }}</span
          >
          <span v-if="numberOfServices != null"
            ><span class="sub-mobile__n">{{ numberOfServices }}</span>
            {{ t('common.lessonsShort') }}</span
          >
          <span v-else class="sub-mobile__inf">∞ {{ t('tabs.subscriptionTypes.unlimited') }}</span>
        </div>
        <div v-if="coveredTypes" class="sub-mobile__types">{{ coveredTypes }}</div>
      </div>
      <span v-if="priceLabel" class="sub-mobile__price"
        >{{ priceLabel }}<span class="sub-mobile__cur">{{ currencySymbol }}</span></span
      >
      <span v-else class="sub-mobile__price sub-mobile__price--none">–</span>
    </div>
  </td>
</template>

<style scoped>
.sub-mobile {
  vertical-align: middle;
}

.sub-mobile__row {
  display: flex;
  align-items: center;
  gap: 0.85rem;
}

.sub-mobile__idx {
  width: 1.5rem;
  flex-shrink: 0;
  text-align: right;
  font-family: var(--font-tufte-sans);
  font-size: 0.8rem;
  color: var(--color-tufte-muted);
  font-variant-numeric: tabular-nums;
}

.sub-mobile__main {
  flex: 1;
  min-width: 0;
}

.sub-mobile__name {
  font-size: 1.04rem;
  line-height: 1.25;
}

.sub-mobile__meta {
  display: flex;
  flex-wrap: wrap;
  gap: 0.1rem 0.7rem;
  margin-top: 0.22rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.78rem;
  color: var(--color-tufte-muted);
}

.sub-mobile__n {
  font-family: var(--font-tufte-mono);
  color: var(--color-tufte-text);
}

.sub-mobile__inf {
  color: var(--color-money-positive);
}

.sub-mobile__types {
  margin-top: 0.22rem;
  font-family: var(--font-tufte-sans);
  font-size: 0.76rem;
  line-height: 1.35;
  color: var(--color-tufte-muted);
}

.sub-mobile__price {
  flex-shrink: 0;
  font-family: var(--font-tufte-mono);
  font-size: 1.02rem;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
  color: var(--color-money-positive);
}

.sub-mobile__price--none {
  color: var(--color-tufte-muted);
  opacity: 0.6;
}

.sub-mobile__cur {
  margin-left: 0.12rem;
  font-size: 0.76rem;
  color: var(--color-tufte-muted);
}
</style>
